import { BACKEND_URL } from "@/lib/api";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import axios from "axios";

const API = `${BACKEND_URL}/api`;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim().toLowerCase() });
    } catch (_) {
      // We intentionally don't surface server errors here — the API returns 200
      // regardless of whether the email exists (anti-enumeration).
    }
    setSent(true);
    setBusy(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel — mirrors /login */}
      <div className="hidden lg:flex relative bg-[#0F172A] text-white p-12 flex-col">
        <Link to="/" className="inline-flex items-center gap-2.5">
          <span className="inline-grid place-items-center h-8 w-8 rounded-md bg-white">
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="#0F172A" strokeWidth="2">
              <path d="M4 7h7M4 12h12M4 17h7" strokeLinecap="round" /><circle cx="18" cy="7" r="2.2" fill="#0055FF" stroke="none" /><circle cx="18" cy="17" r="2.2" fill="#0055FF" stroke="none" />
            </svg>
          </span>
          <span className="font-semibold tracking-tight">Connexis<span className="text-[#0055FF]">.</span></span>
        </Link>
        <div className="mt-auto max-w-md">
          <p className="text-lg tracking-tight leading-snug">"Account security is everything when you're routing real money through the platform."</p>
          <p className="text-sm text-slate-400 mt-3">— Connexis Security Team</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12 bg-white">
        <div className="w-full max-w-md">
          <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900" data-testid="back-to-login">
            <ArrowLeft className="size-3.5" /> Back to sign in
          </Link>
          {!sent ? (
            <>
              <h1 className="mt-6 text-3xl sm:text-4xl tracking-[-0.02em] font-semibold text-slate-900">Reset your password</h1>
              <p className="mt-2.5 text-sm text-slate-600">Enter the email address on your Connexis account and we'll send you a secure reset link. Expires in 60 minutes.</p>
              <form onSubmit={onSubmit} className="mt-7 space-y-4" data-testid="forgot-form">
                <div>
                  <Label htmlFor="email">Email address</Label>
                  <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="forgot-email" />
                </div>
                <Button disabled={busy} type="submit" className="w-full rounded-full h-11" data-testid="forgot-submit">
                  {busy ? "Sending…" : <>Send reset link <ArrowRight className="size-4" /></>}
                </Button>
              </form>
            </>
          ) : (
            <div className="mt-12 text-center" data-testid="forgot-sent">
              <span className="inline-grid place-items-center size-14 rounded-full bg-emerald-50 text-emerald-600 mx-auto">
                <CheckCircle2 className="size-7" strokeWidth={1.6} />
              </span>
              <h1 className="mt-5 text-2xl sm:text-3xl tracking-[-0.02em] font-semibold text-slate-900">Check your inbox</h1>
              <p className="mt-2.5 text-sm text-slate-600 max-w-sm mx-auto">
                If an account exists for <b>{email}</b>, we've sent a reset link. It expires in 60 minutes and can only be used once.
              </p>
              <div className="mt-7 inline-flex items-center gap-2 text-[12.5px] text-slate-500">
                <Mail className="size-3.5" /> Not seeing it? Check spam, or try a different email.
              </div>
              <div className="mt-8">
                <Link to="/login" className="text-sm text-[#0055FF] hover:underline" data-testid="forgot-back-to-login">Return to sign in →</Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
